import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApprovalHistoryTimeline } from './approval-history-timeline';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Search,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  FileText,
  TrendingUp,
  Download,
  File,
  FileSpreadsheet,
  Image,
  Maximize2,
  Settings2,
} from 'lucide-react';
import type { PurchaseRequest, PurchaseRequestStatus, PurchaseRequestSummary, Document } from '@/types/purchase-request';
import type { Employee } from '@/types/index';
import { fetchPurchaseRequests } from '@/lib/mock-purchase-requests';
import { fetchFinOpsPurchaseRequests, fetchFinOpsPurchaseRequestDetails, isFinOpsIntegrationAvailable } from '@/lib/finops-api';
import { getDataMode } from '@/lib/data-service';
import { format } from 'date-fns';
import { PurchaseRequestFullModal } from './purchase-request-full-modal';
import { Link2Off } from 'lucide-react';

// DataTable + pagination
import { DataTableEnhanced, type ColumnDef, type ColumnSort, columnDefinitionsToVisibility } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray } from '@/types/pagination';
import type { PaginationMetadata } from '@/types/pagination';

// Column configurator + preferences
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';

// Advanced filter sheet
import {
  PurchaseRequestFilterSheet,
  type PRFilters,
  countActivePRFilters,
  applyPRFilters,
} from './purchase-request-filter-sheet';

interface PurchasesCommitmentsTabProps {
  projectId: number;
  projectCode?: string;
  employees?: Employee[];
}

// ─── Status config ─────────────────────────────────────────────────────────────

const statusConfig: Record<string, {
  variant: 'success' | 'warning' | 'destructive' | 'default';
  accent: 'success' | 'warning' | 'destructive' | 'info';
  icon: typeof CheckCircle2;
}> = {
  Approved:   { variant: 'success',      accent: 'success',     icon: CheckCircle2 },
  Pending:    { variant: 'warning',      accent: 'warning',     icon: Clock },
  Rejected:   { variant: 'destructive',  accent: 'destructive', icon: XCircle },
  Posted:     { variant: 'default',      accent: 'info',        icon: CheckCircle2 },
  Redirected: { variant: 'warning',      accent: 'warning',     icon: AlertCircle },
  Reopened:   { variant: 'warning',      accent: 'warning',     icon: AlertCircle },
  Cancelled:  { variant: 'destructive',  accent: 'destructive', icon: XCircle },
  Draft:      { variant: 'default',      accent: 'info',        icon: FileText },
};

const getStatusConfig = (status: string) =>
  statusConfig[status] ?? { variant: 'default' as const, accent: 'info' as const, icon: AlertCircle };

// ─── Document helpers ──────────────────────────────────────────────────────────

const getDocumentIcon = (type: Document['type']) => {
  switch (type) {
    case 'pdf':         return FileText;
    case 'image':       return Image;
    case 'spreadsheet': return FileSpreadsheet;
    case 'document':    return FileText;
    default:            return File;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// ─── PR helpers ────────────────────────────────────────────────────────────────

const calculatePRAge = (request: PurchaseRequest): number | null => {
  const finalStatuses = ['Approved', 'Posted', 'Rejected', 'Cancelled'];
  if (finalStatuses.includes(request.status)) return null;
  const diffTime = Math.abs(Date.now() - new Date(request.date).getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getCurrentApprovalStage = (request: PurchaseRequest): string => {
  if (!request.approvalHistory || request.approvalHistory.length === 0) return '';
  const sorted = [...request.approvalHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return sorted[0].stage || '';
};

// ─── Default column definitions ────────────────────────────────────────────────

const DEFAULT_PR_COLUMNS: ColumnDefinition[] = [
  { id: 'requestId',     name: 'Request ID',     description: 'Purchase request identifier',    category: 'Status',    visible: true,  locked: true, order: 0 },
  { id: 'orderNumber',   name: 'Order Number',   description: 'Associated PO numbers',          category: 'Status',    visible: true,  order: 1 },
  { id: 'description',   name: 'Description',    description: 'Request description',            category: 'Status',    visible: true,  locked: true, order: 2 },
  { id: 'category',      name: 'Category',       description: 'Purchase category',              category: 'Status',    visible: true,  order: 3 },
  { id: 'requestedBy',   name: 'Requested By',   description: 'Employee who raised the request',category: 'Resource',  visible: true,  order: 4 },
  { id: 'currentStage',  name: 'Current Stage',  description: 'Current approval stage',         category: 'Status',    visible: true,  order: 5 },
  { id: 'costCentre',    name: 'Cost Centre',    description: 'Cost centre allocation',         category: 'Financial', visible: true,  order: 6 },
  { id: 'status',        name: 'Status',         description: 'Approval status',                category: 'Status',    visible: true,  locked: true, order: 7 },
  { id: 'amount',        name: 'Amount',         description: 'Request amount',                 category: 'Financial', visible: true,  locked: true, order: 8 },
  { id: 'supplier',      name: 'Supplier',       description: 'Supplier name',                  category: 'Resource',  visible: true,  order: 9 },
  { id: 'date',          name: 'Date',           description: 'Request date',                   category: 'Timeline',  visible: true,  order: 10 },
  { id: 'daysOpen',      name: 'Days Open',      description: 'Age of open requests',           category: 'Timeline',  visible: true,  order: 11 },
];

// ─── Main component ────────────────────────────────────────────────────────────

export function PurchasesCommitmentsTab({ projectId, projectCode, employees = [] }: PurchasesCommitmentsTabProps) {
  const { currentCompany } = useCompany();

  // ── Data state ────────────────────────────────────────────────────────
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFinOpsMode, setIsFinOpsMode] = useState(false);
  const [finOpsNotConfigured, setFinOpsNotConfigured] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [fullModalOpen, setFullModalOpen] = useState(false);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryRequest, setApprovalHistoryRequest] = useState<PurchaseRequest | null>(null);

  // ── Search / filter state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<PRFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // ── Table state ───────────────────────────────────────────────────────
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // ── Column preferences ────────────────────────────────────────────────
  const { columns: prColumns, updateColumns } = useColumnPreferences({
    storageKey: 'purchase-requests-columns',
    defaultColumns: DEFAULT_PR_COLUMNS,
  });

  const visibleColumns = useMemo(
    () => prColumns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [prColumns]
  );

  // ─── Employee name resolver ──────────────────────────────────────────────────

  const getEmployeeNameByExternalUserId = (externalUserId: string): string => {
    if (!externalUserId) return 'Unknown Employee';
    const employee = employees.find(
      (e) => e.externalUserId?.toLowerCase() === externalUserId.toLowerCase()
    );
    return employee?.name || externalUserId;
  };

  // ─── Fetch data ──────────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setFinOpsNotConfigured(false);

      const dataMode = getDataMode();
      const companyId = currentCompany?.id;
      const financialsInstanceId = currentCompany?.financialsInstanceId;

      const finOpsAvailable = dataMode === 'live'
        ? !!financialsInstanceId
        : isFinOpsIntegrationAvailable(companyId);

      if (dataMode === 'live' && projectCode) {
        if (finOpsAvailable) {
          setIsFinOpsMode(true);
          try {
            const finOpsData = await fetchFinOpsPurchaseRequests(projectCode, projectId, companyId, financialsInstanceId);
            setRequests(finOpsData ?? []);
          } catch {
            console.warn('[PurchasesCommitmentsTab] FinOps API unavailable, showing empty state');
            setRequests([]);
          }
          return;
        } else {
          setFinOpsNotConfigured(true);
          setRequests([]);
          return;
        }
      }

      if (dataMode === 'live') {
        setRequests([]);
        return;
      }

      setIsFinOpsMode(false);
      const data = await fetchPurchaseRequests(projectId);
      setRequests(data);
    } catch (err) {
      setError('Unable to load purchase data. Please try again later.');
      console.error('Error fetching purchase requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [projectId, projectCode, currentCompany?.id, currentCompany?.financialsInstanceId]);

  // ─── Derived data ────────────────────────────────────────────────────────────

  const suppliers = useMemo(() => {
    const unique = Array.from(new Set(requests.map((r) => r.supplier).filter(Boolean))).sort();
    return unique.map((s) => {
      const name = getEmployeeNameByExternalUserId(s);
      // label used for search — includes both code and name so both are searchable
      return { value: s, label: `${s} — ${name}`, code: s, name };
    });
  }, [requests, employees]);

  const categories = useMemo(
    () => Array.from(new Set(requests.map((r) => r.category).filter(Boolean))).sort(),
    [requests]
  );

  const statuses = useMemo(
    () => Array.from(new Set(requests.map((r) => r.status).filter(Boolean))).sort(),
    [requests]
  );

  const summary: PurchaseRequestSummary = useMemo(() => {
    const approved = requests.filter((r) => r.status === 'Approved');
    return {
      totalCommittedCost: approved.reduce((s, r) => s + r.amount, 0),
      totalApprovedRequests: approved.length,
      totalPendingRequests: requests.filter((r) => r.status === 'Pending').length,
      totalRejectedRequests: requests.filter((r) => r.status === 'Rejected').length,
      averageRequestAmount: requests.length > 0
        ? requests.reduce((s, r) => s + r.amount, 0) / requests.length
        : 0,
    };
  }, [requests]);

  const activeFilterCount = countActivePRFilters(advancedFilters);

  // ─── Filter + sort ───────────────────────────────────────────────────────────

  const filteredAndSorted = useMemo(() => {
    // 1. Text search
    let result = requests.filter((r) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        r.requestId.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.supplier.toLowerCase().includes(q) ||
        getEmployeeNameByExternalUserId(r.supplier).toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        r.requestedBy.toLowerCase().includes(q) ||
        getEmployeeNameByExternalUserId(r.requestedBy).toLowerCase().includes(q)
      );
    });

    // 2. Advanced filters
    result = applyPRFilters(result, advancedFilters, getEmployeeNameByExternalUserId);

    // 3. Multi-column sort
    result = [...result].sort((a, b) => {
      for (const sort of columnSorts) {
        let aVal: string | number;
        let bVal: string | number;

        switch (sort.columnId) {
          case 'requestId':    aVal = a.requestId;    bVal = b.requestId;    break;
          case 'description':  aVal = a.description;  bVal = b.description;  break;
          case 'status':       aVal = a.status;       bVal = b.status;       break;
          case 'amount':       aVal = a.amount;       bVal = b.amount;       break;
          case 'supplier':
            aVal = getEmployeeNameByExternalUserId(a.supplier);
            bVal = getEmployeeNameByExternalUserId(b.supplier);
            break;
          case 'date':
            aVal = new Date(a.date).getTime();
            bVal = new Date(b.date).getTime();
            break;
          case 'requestedBy':
            aVal = getEmployeeNameByExternalUserId(a.requestedBy);
            bVal = getEmployeeNameByExternalUserId(b.requestedBy);
            break;
          case 'category':     aVal = a.category;     bVal = b.category;     break;
          case 'currentStage':
            aVal = getCurrentApprovalStage(a);
            bVal = getCurrentApprovalStage(b);
            break;
          case 'costCentre':   aVal = a.costCentreCode ?? ''; bVal = b.costCentreCode ?? ''; break;
          default: continue;
        }

        const cmp =
          typeof aVal === 'number' && typeof bVal === 'number'
            ? aVal - bVal
            : String(aVal).localeCompare(String(bVal));

        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });

    return result;
  }, [requests, searchQuery, advancedFilters, columnSorts, employees]);

  // Reset page on filter/sort change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, advancedFilters, columnSorts, itemsPerPage]);

  // ─── Pagination ──────────────────────────────────────────────────────────────

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(filteredAndSorted.length, currentPage, itemsPerPage),
    [filteredAndSorted.length, currentPage, itemsPerPage]
  );
  const paginatedRequests = useMemo(
    () => paginateArray(filteredAndSorted, currentPage, itemsPerPage).data,
    [filteredAndSorted, currentPage, itemsPerPage]
  );

  // ─── Column definitions ──────────────────────────────────────────────────────

  const columnDefs = useMemo((): ColumnDef<PurchaseRequest, unknown>[] => {
    return visibleColumns.map((col): ColumnDef<PurchaseRequest, unknown> => {
      switch (col.id) {
        case 'requestId':
          return {
            id: 'requestId',
            header: col.name,
            accessorFn: (r) => r.requestId,
            cell: ({ row }) => (
              <span
                className="font-medium text-primary hover:underline cursor-pointer"
                onClick={() => { setSelectedRequest(row.original); setShowDocuments(false); }}
              >
                {row.original.requestId}
              </span>
            ),
          };

        case 'orderNumber':
          return {
            id: 'orderNumber',
            header: col.name,
            enableSorting: false,
            accessorFn: (r) => r.poNumber?.[0] ?? '',
            cell: ({ row }) => {
              const po = row.original.poNumber;
              if (!po || po.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
              if (po.length === 1) return <span className="text-sm">{po[0]}</span>;
              return (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm truncate">{po[0]}</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="cursor-pointer hover:bg-secondary/80 text-xs px-1.5 py-0 h-5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        +{po.length - 1} more
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="start">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Purchase Orders</p>
                        {po.map((p, i) => <div key={i} className="text-sm font-mono">{p}</div>)}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              );
            },
          };

        case 'description':
          return {
            id: 'description',
            header: col.name,
            accessorFn: (r) => r.description,
            cell: ({ row }) => (
              <span className="max-w-[300px] truncate block">{row.original.description}</span>
            ),
          };

        case 'category':
          return {
            id: 'category',
            header: col.name,
            accessorFn: (r) => r.category,
            cell: ({ row }) => <span className="text-sm">{row.original.category}</span>,
          };

        case 'requestedBy':
          return {
            id: 'requestedBy',
            header: col.name,
            accessorFn: (r) => getEmployeeNameByExternalUserId(r.requestedBy),
            cell: ({ row }) => (
              <span className="text-sm">{getEmployeeNameByExternalUserId(row.original.requestedBy)}</span>
            ),
          };

        case 'currentStage':
          return {
            id: 'currentStage',
            header: col.name,
            accessorFn: (r) => getCurrentApprovalStage(r),
            cell: ({ row }) => (
              <span className="text-sm text-muted-foreground">
                {getCurrentApprovalStage(row.original) || '—'}
              </span>
            ),
          };

        case 'costCentre':
          return {
            id: 'costCentre',
            header: col.name,
            accessorFn: (r) => r.costCentreCode ?? '',
            cell: ({ row }) => {
              const r = row.original;
              if (!r.costCentreCode) return <span className="text-muted-foreground">—</span>;
              return (
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{r.costCentreCode}</span>
                  {r.costCentreName && (
                    <span className="text-xs text-muted-foreground">{r.costCentreName}</span>
                  )}
                </div>
              );
            },
          };

        case 'status':
          return {
            id: 'status',
            header: col.name,
            enableSorting: false,
            accessorFn: (r) => r.status,
            cell: ({ row }) => {
              const cfg = getStatusConfig(row.original.status);
              const Icon = cfg.icon;
              return (
                <Badge
                  variant={cfg.variant}
                  accent={cfg.accent}
                  className="gap-1 cursor-pointer hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setApprovalHistoryRequest(row.original);
                    setApprovalHistorySheetOpen(true);
                  }}
                >
                  <Icon className="h-3 w-3" />
                  {row.original.status}
                </Badge>
              );
            },
          };

        case 'amount':
          return {
            id: 'amount',
            header: col.name,
            accessorFn: (r) => r.amount,
            cell: ({ row }) => (
              <span className="text-right font-medium block">
                {formatCurrency(row.original.amount, row.original.currency)}
              </span>
            ),
          };

        case 'supplier': {
          return {
            id: 'supplier',
            header: col.name,
            accessorFn: (r) => getEmployeeNameByExternalUserId(r.supplier),
            cell: ({ row }) => {
              const code = row.original.supplier;
              const name = getEmployeeNameByExternalUserId(code);
              return (
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{name}</span>
                  <span className="text-xs text-muted-foreground">{code}</span>
                </div>
              );
            },
          };
        }

        case 'date':
          return {
            id: 'date',
            header: col.name,
            accessorFn: (r) => r.date,
            cell: ({ row }) => (
              <span className="text-sm">{format(new Date(row.original.date), 'dd MMM yyyy')}</span>
            ),
          };

        case 'daysOpen':
          return {
            id: 'daysOpen',
            header: col.name,
            accessorFn: (r) => calculatePRAge(r) ?? -1,
            cell: ({ row }) => {
              const age = calculatePRAge(row.original);
              if (age === null) return <span className="text-muted-foreground text-sm">—</span>;
              const isOld = age > 30;
              return (
                <Badge
                  variant={isOld ? 'destructive' : 'default'}
                  accent={isOld ? 'destructive' : 'info'}
                  className="text-xs"
                >
                  {age} {age === 1 ? 'day' : 'days'}
                </Badge>
              );
            },
          };

        default:
          return {
            id: col.id,
            header: col.name,
            accessorFn: () => '',
            cell: () => null,
          };
      }
    });
  }, [visibleColumns, employees]);

  // ─── Currency formatter ──────────────────────────────────────────────────────

  const formatCurrency = (amount: number, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount) + ` ${currency}`;
  };

  // ─── Loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2"><Skeleton className="h-4 w-32" /></CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button onClick={loadData}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── FinOps not configured ────────────────────────────────────────────────────

  if (finOpsNotConfigured) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">FinOps Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  The FinOps integration has not been set up for this company. Please configure the
                  integration in Settings to view purchase requests.
                </p>
              </div>
              <Button variant="outline" onClick={loadData}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Empty state ──────────────────────────────────────────────────────────────

  if (requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Purchase Requests</h3>
                <p className="text-sm text-muted-foreground">
                  There are no purchase requests associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Committed Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.totalCommittedCost)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              From {summary.totalApprovedRequests} approved requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Approved Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <div className="text-2xl font-bold">{summary.totalApprovedRequests}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {requests.length > 0
                ? ((summary.totalApprovedRequests / requests.length) * 100).toFixed(0)
                : 0}% of total requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              <div className="text-2xl font-bold">{summary.totalPendingRequests}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average Request</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.averageRequestAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Across all requests</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Requests</CardTitle>
          <CardDescription>
            View purchase requests{currentCompany ? ` from ${currentCompany.name}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-4">
            {/* Search */}
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>

            {/* Advanced Filter button */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 relative shrink-0"
              aria-label="Advanced Filters"
              onClick={() => setFilterSheetOpen(true)}
            >
              <Filter className="h-3.5 w-3.5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {/* Column configurator button */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              aria-label="Configure columns"
              onClick={() => setColumnConfigOpen(true)}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-3">
            Showing {paginatedRequests.length} of {filteredAndSorted.length} requests
          </p>

          {/* Table */}
          <DataTableEnhanced<PurchaseRequest, unknown>
            columns={columnDefs}
            data={paginatedRequests}
            columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
            columnSorts={columnSorts}
            onColumnSortChange={(s) => { setColumnSorts(s); setCurrentPage(1); }}
            emptyMessage={
              <div className="flex flex-col items-center gap-2 py-2">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No requests match your filters</p>
              </div>
            }
            paginationContent={
              <PaginationControls
                pagination={pagination}
                onPageChange={setCurrentPage}
                onPageSizeChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
              />
            }
          />
        </CardContent>
      </Card>

      {/* Detail Side Sheet */}
      <Sheet open={!!selectedRequest} onOpenChange={(open) => {
        if (!open) { setSelectedRequest(null); setShowDocuments(false); }
      }}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedRequest && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      <SheetTitle>{selectedRequest.requestId}</SheetTitle>
                    </div>
                    <SheetDescription>Purchase Request Details</SheetDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedRequest(null);
                      setFullModalOpen(true);
                      setTimeout(() => setSelectedRequest(selectedRequest), 100);
                    }}
                    title="Expand to full view"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Status Badge */}
                {(() => {
                  const cfg = getStatusConfig(selectedRequest.status);
                  const Icon = cfg.icon;
                  return (
                    <Badge variant={cfg.variant} accent={cfg.accent} className="gap-1">
                      <Icon className="h-3 w-3" />
                      {selectedRequest.status}
                    </Badge>
                  );
                })()}

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Description</p>
                    <p className="font-medium">{selectedRequest.description}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Amount</p>
                    <p className="font-medium">{formatCurrency(selectedRequest.amount, selectedRequest.currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Supplier</p>
                    <p className="font-medium">{getEmployeeNameByExternalUserId(selectedRequest.supplier)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Date</p>
                    <p className="font-medium">{format(new Date(selectedRequest.date), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Category</p>
                    <p className="font-medium">{selectedRequest.category}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Requested By</p>
                    <p className="font-medium">{getEmployeeNameByExternalUserId(selectedRequest.requestedBy)}</p>
                  </div>
                  {selectedRequest.costCentreCode && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-0.5">Cost Centre</p>
                      <p className="font-medium">{selectedRequest.costCentreCode}{selectedRequest.costCentreName ? ` — ${selectedRequest.costCentreName}` : ''}</p>
                    </div>
                  )}
                  {selectedRequest.poNumber && selectedRequest.poNumber.length > 0 && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-0.5">Order Numbers</p>
                      <p className="font-medium">{selectedRequest.poNumber.join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedRequest.notes && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Notes</p>
                    <p className="text-sm bg-muted/30 rounded p-3">{selectedRequest.notes}</p>
                  </div>
                )}

                {/* Documents */}
                {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">Documents ({selectedRequest.documents.length})</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs"
                        onClick={() => setShowDocuments(!showDocuments)}
                      >
                        {showDocuments ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showDocuments && (
                      <div className="space-y-2">
                        {selectedRequest.documents.map((doc) => {
                          const DocIcon = getDocumentIcon(doc.type);
                          return (
                            <div key={doc.id} className="flex items-center gap-3 p-2 rounded border bg-muted/20">
                              <DocIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</p>
                              </div>
                              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild>
                                <a href={doc.url} download>
                                  <Download className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Approval history */}
                {selectedRequest.approvalHistory && selectedRequest.approvalHistory.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Approval History</p>
                    <ApprovalHistoryTimeline
                      approvalHistory={selectedRequest.approvalHistory}
                      requestId={selectedRequest.requestId}
                    />
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t">
                <Button
                  className="w-full gap-2"
                  onClick={() => {
                    setSelectedRequest(null);
                    setFullModalOpen(true);
                    setTimeout(() => setSelectedRequest(selectedRequest), 100);
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Full View
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Full-screen modal */}
      <Dialog open={fullModalOpen} onOpenChange={setFullModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRequest?.requestId}</DialogTitle>
            <DialogDescription>Full Purchase Request Details</DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <PurchaseRequestFullModal
              purchaseRequest={selectedRequest}
              allPurchaseRequests={requests}
              open={fullModalOpen}
              onOpenChange={setFullModalOpen}
              onPurchaseRequestChange={setSelectedRequest}
              onFetchLineDetails={projectCode
                ? (_code, instanceId) => fetchFinOpsPurchaseRequestDetails(projectCode, instanceId)
                : undefined}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Approval History Sheet */}
      <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
        <SheetContent className="sm:max-w-[500px] p-0 flex flex-col">
          {approvalHistoryRequest && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4 border-b">
                <SheetTitle>Approval History</SheetTitle>
                <SheetDescription>{approvalHistoryRequest.requestId} — {approvalHistoryRequest.description}</SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <ApprovalHistoryTimeline
                  approvalHistory={approvalHistoryRequest.approvalHistory ?? []}
                  requestId={approvalHistoryRequest.requestId}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={prColumns}
        onSave={updateColumns}
        pageKey="purchase-requests"
      />

      {/* Advanced Filter Sheet */}
      <PurchaseRequestFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={advancedFilters}
        onApply={(f) => { setAdvancedFilters(f); setCurrentPage(1); }}
        onClear={() => { setAdvancedFilters({}); setCurrentPage(1); }}
        availableStatuses={statuses}
        availableSuppliers={suppliers}
        availableCategories={categories}
      />
    </div>
  );
}
